import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from './Navbar';
import './Helpdesk.css';

const Helpdesk = () => {
    const [formData, setFormData] = useState({
        subject: '',
        form_type: 'general',
        form_name: '',
        description: '',
        due_date: '',
        status: 'Open'
    });

    const [tickets, setTickets] = useState([]);
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingForms, setLoadingForms] = useState(false);
    const [showTicketList, setShowTicketList] = useState(() => {
        const saved = sessionStorage.getItem('helpdesk_showTicketList');
        return saved !== null ? JSON.parse(saved) : true;
    });
    const [selectedTicket, setSelectedTicket] = useState(() => {
        const saved = sessionStorage.getItem('helpdesk_selectedTicket');
        return saved ? JSON.parse(saved) : null;
    });
    const [message, setMessage] = useState({ type: '', text: '' });
    const [authToken, setAuthToken] = useState('');
    const [initialLoad, setInitialLoad] = useState(true);

    // Environment variable for API URL
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const API_URL = `${API_BASE_URL}/api`;

    useEffect(() => {
        const token = sessionStorage.getItem('token');
        
        if (token) {
            setAuthToken(token);
            console.log('✅ Auth token loaded');
            
            createTables(token);
            fetchTickets(token);
        } else {
            console.warn('⚠️ No auth token found');
            showMessage('error', 'Authentication required. Please login again.');
        }
        
        fetchForms();
        
        setInitialLoad(false);
    }, []);

    const fetchForms = async () => {
        try {
            setLoadingForms(true);
            console.log('📡 Fetching forms from:', `${API_URL}/forms`);
            
            const response = await axios.get(`${API_URL}/forms`);
            
            console.log('📥 Forms response:', response.data);
            
            let formsData = [];
            
            if (response.data && response.data.data && Array.isArray(response.data.data)) {
                formsData = response.data.data;
                console.log('📋 Found forms in response.data.data');
            }
            else if (response.data && response.data.forms && Array.isArray(response.data.forms)) {
                formsData = response.data.forms;
                console.log('📋 Found forms in response.data.forms');
            }
            else if (Array.isArray(response.data)) {
                formsData = response.data;
                console.log('📋 Found forms in response.data (array)');
            }
            
            console.log(`✅ Found ${formsData.length} forms`);
            
            if (formsData.length > 0) {
                console.log('📋 First few forms:', formsData.slice(0, 3));
                setForms(formsData);
            } else {
                console.warn('⚠️ Forms array is empty');
                setForms([]);
            }
            
        } catch (error) {
            console.error('❌ Error fetching forms:', error);
            console.error('❌ Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            showMessage('error', 'Failed to fetch forms');
            setForms([]);
        } finally {
            setLoadingForms(false);
        }
    };

    const createTables = async (token) => {
        try {
            await axios.post(`${API_URL}/create-tables`, {}, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            console.log('✅ Tables created successfully');
        } catch (error) {
            console.error('❌ Error creating tables:', error);
        }
    };

    const fetchTickets = async (token) => {
        try {
            console.log('📡 Fetching tickets...');
            
            if (!token) {
                console.warn('⚠️ No token available');
                return;
            }
            
            const response = await axios.get(`${API_URL}/my-tickets`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.data && response.data.success) {
                const ticketsData = response.data.tickets || [];
                console.log(`✅ Found ${ticketsData.length} tickets`);
                setTickets(ticketsData);
                
                if (selectedTicket) {
                    const updatedTicket = ticketsData.find(t => t.id === selectedTicket.id);
                    if (updatedTicket) {
                        setSelectedTicket(updatedTicket);
                    }
                }
            }
        } catch (error) {
            console.error('❌ Error fetching tickets:', error);
            if (error.response?.status === 401) {
                showMessage('error', 'Session expired. Please login again.');
                sessionStorage.removeItem('token');
                sessionStorage.removeItem('user');
            }
        }
    };

    const getAxiosConfig = () => {
        const token = sessionStorage.getItem('token');
        return {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        };
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            const token = sessionStorage.getItem('token');
            if (!token) {
                showMessage('error', 'Authentication required. Please login again.');
                setLoading(false);
                return;
            }

            const ticketData = {
                subject: formData.subject,
                form_type: formData.form_type,
                form_name: formData.form_name,
                description: formData.description,
                due_date: formData.due_date,
                status: 'Open'
            };

            console.log('📤 Submitting ticket:', ticketData);

            const response = await axios.post(
                `${API_URL}/tickets`, 
                ticketData,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.data.success) {
                showMessage('success', `✅ Ticket created! ID: ${response.data.ticket.ticket_id}`);
                setFormData({
                    subject: '',
                    form_type: 'general',
                    form_name: '',
                    description: '',
                    due_date: '',
                    status: 'Open'
                });
                
                await fetchTickets(token);
                setShowTicketList(true);
                sessionStorage.setItem('helpdesk_showTicketList', JSON.stringify(true));
            }
        } catch (error) {
            console.error('❌ Error creating ticket:', error);
            showMessage('error', 'Failed to create ticket');
        } finally {
            setLoading(false);
        }
    };

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    };

    const viewTicketDetails = (ticket) => {
        if (selectedTicket && selectedTicket.id === ticket.id) {
            setSelectedTicket(null);
            sessionStorage.removeItem('helpdesk_selectedTicket');
        } else {
            setSelectedTicket(ticket);
            sessionStorage.setItem('helpdesk_selectedTicket', JSON.stringify(ticket));
        }
    };

    const toggleTicketListView = (show) => {
        setShowTicketList(show);
        sessionStorage.setItem('helpdesk_showTicketList', JSON.stringify(show));
        if (!show) {
            setSelectedTicket(null);
            sessionStorage.removeItem('helpdesk_selectedTicket');
        }
    };

    const getStatusColor = (status) => {
        switch(status?.toLowerCase()) {
            case 'open': return '#28a745';
            case 'closed': return '#6c757d';
            default: return '#007bff';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch (e) {
            return dateString;
        }
    };

    const ClosedTicketDetails = ({ ticket }) => {
        if (ticket.status !== 'Closed') return null;
        
        return (
            <div style={{
                marginTop: '20px',
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                borderLeft: '4px solid #6c757d'
            }}>
                <h5 style={{ margin: '0 0 15px 0', color: '#6c757d' }}>
                    🔒 Ticket Closure Details
                </h5>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                    <div>
                        <span style={{ fontSize: '12px', color: '#7f8c8d' }}>Closed By</span>
                        <p style={{ margin: '5px 0 0', fontWeight: '500' }}>
                            {ticket.closed_by_name || ticket.closed_by || 'N/A'}
                        </p>
                    </div>
                    <div>
                        <span style={{ fontSize: '12px', color: '#7f8c8d' }}>Closed Date</span>
                        <p style={{ margin: '5px 0 0', fontWeight: '500' }}>
                            {formatDateTime(ticket.closed_date)}
                        </p>
                    </div>
                    <div>
                        <span style={{ fontSize: '12px', color: '#7f8c8d' }}>Days Since Closed</span>
                        <p style={{ margin: '5px 0 0', fontWeight: '500' }}>
                            {ticket.closed_date ? (() => {
                                const diff = Math.ceil((new Date() - new Date(ticket.closed_date)) / (1000 * 60 * 60 * 24));
                                return `${diff} day${diff > 1 ? 's' : ''} ago`;
                            })() : 'N/A'}
                        </p>
                    </div>
                </div>
                {ticket.closed_by_name?.toLowerCase().includes('super') && (
                    <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
                        👑 Closed by Super Administrator
                    </div>
                )}
            </div>
        );
    };

    if (initialLoad) {
        return (
            <>
                <Navbar />
                <div style={{
                    minHeight: '100vh',
                    background: '#f8f9fa',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingTop: '70px'
                }}>
                    <div>Loading...</div>
                </div>
            </>
        );
    }

    return (
        <>
            <Navbar />
            <div style={{
                minHeight: '100vh',
                background: '#f8f9fa',
                paddingTop: '70px', // Account for navbar height
                width: '100%'
            }}>
                <div style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    padding: '20px 24px'
                }}>
                    <div className="helpdesk-header" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '24px',
                        flexWrap: 'wrap',
                        gap: '15px'
                    }}>
                        <h1 style={{ margin: 0, fontSize: '28px', color: '#2c3e50' }}>Support & Help Desk</h1>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button 
                                onClick={() => toggleTicketListView(true)}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: showTicketList ? '#28a745' : '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = showTicketList ? '#218838' : '#5a6268';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = showTicketList ? '#28a745' : '#6c757d';
                                }}
                            >
                                <span>📋</span> 
                                {showTicketList ? 'My Tickets' : 'View My Tickets'} 
                                {tickets.length > 0 && ` (${tickets.length})`}
                            </button>
                            
                            <button 
                                onClick={() => toggleTicketListView(false)}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: !showTicketList ? '#28a745' : '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = !showTicketList ? '#218838' : '#0056b3';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = !showTicketList ? '#28a745' : '#007bff';
                                }}
                            >
                                <span>➕</span>
                                {!showTicketList ? 'Create Ticket' : 'Create New Ticket'}
                            </button>
                        </div>
                    </div>

                    {message.text && (
                        <div style={{
                            padding: '12px 20px',
                            borderRadius: '6px',
                            marginBottom: '20px',
                            backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
                            color: message.type === 'success' ? '#155724' : '#721c24',
                            border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
                        }}>
                            {message.text}
                        </div>
                    )}

                    {!showTicketList ? (
                        // ============ CREATE TICKET FORM ============
                        <div style={{
                            background: 'white',
                            padding: '30px',
                            borderRadius: '10px',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
                        }}>
                            <h2 style={{ marginTop: 0, marginBottom: '24px', color: '#2c3e50' }}>Create New Ticket</h2>
                            <form onSubmit={handleSubmit}>
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#34495e' }}>Subject/Title *</label>
                                    <input
                                        type="text"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleInputChange}
                                        placeholder="Enter ticket subject"
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '10px 14px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            transition: 'border-color 0.2s'
                                        }}
                                    />
                                </div>
                                
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#34495e' }}>Form Type *</label>
                                    <select
                                        name="form_type"
                                        value={formData.form_type}
                                        onChange={handleInputChange}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '10px 14px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            background: 'white'
                                        }}
                                    >
                                        <option value="general">General</option>
                                        <option value="form">Form</option>
                                    </select>
                                </div>
                                
                                {formData.form_type === 'form' && (
                                    <div style={{ marginBottom: '20px' }}>
                                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#34495e' }}>Form Name *</label>
                                        <select
                                            name="form_name"
                                            value={formData.form_name}
                                            onChange={handleInputChange}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '10px 14px',
                                                border: '1px solid #ddd',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                background: 'white'
                                            }}
                                        >
                                            <option value="">-- Select Form --</option>
                                            {forms.length > 0 ? (
                                                forms.map((form) => (
                                                    <option key={form.id} value={form.form_name}>
                                                        {form.form_name}
                                                    </option>
                                                ))
                                            ) : (
                                                <option value="" disabled>
                                                    {loadingForms ? 'Loading forms...' : 'No forms available'}
                                                </option>
                                            )}
                                        </select>
                                        {forms.length === 0 && !loadingForms && (
                                            <p style={{ color: '#e74c3c', fontSize: '12px', marginTop: '5px' }}>
                                                ⚠️ No forms found in database. Please create a form first.
                                            </p>
                                        )}
                                        {forms.length > 0 && (
                                            <p style={{ color: '#27ae60', fontSize: '12px', marginTop: '5px' }}>
                                                ✅ {forms.length} form(s) available
                                            </p>
                                        )}
                                    </div>
                                )}
                                
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#34495e' }}>Description</label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        placeholder="Describe your issue or request"
                                        rows="4"
                                        style={{
                                            width: '100%',
                                            padding: '10px 14px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            resize: 'vertical'
                                        }}
                                    />
                                </div>
                                
                                <div style={{ marginBottom: '24px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#34495e' }}>Due Date</label>
                                    <input
                                        type="date"
                                        name="due_date"
                                        value={formData.due_date}
                                        onChange={handleInputChange}
                                        min={new Date().toISOString().split('T')[0]}
                                        style={{
                                            width: '100%',
                                            padding: '10px 14px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                                
                                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                                    <button 
                                        type="submit" 
                                        disabled={loading || (formData.form_type === 'form' && !formData.form_name)}
                                        style={{
                                            padding: '12px 30px',
                                            backgroundColor: (loading || (formData.form_type === 'form' && !formData.form_name)) ? '#6c757d' : '#28a745',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: (loading || (formData.form_type === 'form' && !formData.form_name)) ? 'not-allowed' : 'pointer',
                                            fontWeight: '600',
                                            fontSize: '16px',
                                            flex: '1',
                                            minWidth: '150px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        {loading ? '⏳ Creating...' : '✅ Submit Ticket'}
                                    </button>
                                    
                                    <button 
                                        type="button"
                                        onClick={() => toggleTicketListView(true)}
                                        style={{
                                            padding: '12px 30px',
                                            backgroundColor: '#3498db',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontWeight: '600',
                                            fontSize: '16px',
                                            flex: '1',
                                            minWidth: '150px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px'
                                        }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#2980b9'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = '#3498db'}
                                    >
                                        <span>📋</span> View My Tickets {tickets.length > 0 && `(${tickets.length})`}
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : (
                        // ============ TICKET LIST VIEW ============
                        <div style={{
                            background: 'white',
                            padding: '24px',
                            borderRadius: '10px',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                                <h2 style={{ margin: 0, color: '#2c3e50' }}>My Tickets</h2>
                                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                    <button 
                                        onClick={() => {
                                            toggleTicketListView(false);
                                            fetchForms();
                                        }}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: '#28a745',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            fontWeight: '500',
                                            fontSize: '14px'
                                        }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#218838'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = '#28a745'}
                                    >
                                        <span>➕</span> Create New Ticket
                                    </button>
                                    <button 
                                        onClick={() => {
                                            fetchTickets(sessionStorage.getItem('token'));
                                            fetchForms();
                                        }}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: '#6c757d',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            fontWeight: '500',
                                            fontSize: '14px'
                                        }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#5a6268'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = '#6c757d'}
                                    >
                                        <span>🔄</span> Refresh
                                    </button>
                                </div>
                            </div>
                            
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    fontSize: '14px'
                                }}>
                                    <thead>
                                        <tr style={{
                                            backgroundColor: '#f8f9fa',
                                            borderBottom: '2px solid #e9ecef'
                                        }}>
                                            <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Ticket ID</th>
                                            <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Subject</th>
                                            <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Form Type</th>
                                            <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Form Name</th>
                                            <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Due Date</th>
                                            <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Status</th>
                                            <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Created</th>
                                            <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Closed By</th>
                                            <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Closed Date</th>
                                            <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: '600', color: '#495057' }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tickets.length === 0 ? (
                                            <tr>
                                                <td colSpan="10" style={{ textAlign: 'center', padding: '40px 20px' }}>
                                                    <p style={{ fontSize: '16px', color: '#7f8c8d', marginBottom: '15px' }}>
                                                        No tickets found
                                                    </p>
                                                    <button 
                                                        onClick={() => {
                                                            toggleTicketListView(false);
                                                            fetchForms();
                                                        }}
                                                        style={{
                                                            padding: '10px 20px',
                                                            backgroundColor: '#28a745',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '8px',
                                                            fontWeight: '500'
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#218838'}
                                                        onMouseLeave={(e) => e.target.style.backgroundColor = '#28a745'}
                                                    >
                                                        <span>➕</span> Create Your First Ticket
                                                    </button>
                                                </td>
                                            </tr>
                                        ) : (
                                            tickets.map((ticket) => (
                                                <React.Fragment key={ticket.id}>
                                                    <tr 
                                                        onClick={() => viewTicketDetails(ticket)}
                                                        style={{ 
                                                            cursor: 'pointer',
                                                            backgroundColor: selectedTicket?.id === ticket.id ? '#f0f7ff' : 'transparent',
                                                            transition: 'background-color 0.2s',
                                                            borderBottom: '1px solid #e9ecef'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (selectedTicket?.id !== ticket.id) {
                                                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (selectedTicket?.id !== ticket.id) {
                                                                e.currentTarget.style.backgroundColor = 'transparent';
                                                            }
                                                        }}
                                                    >
                                                        <td style={{ padding: '12px 10px' }}>
                                                            <span style={{ color: '#3498db', fontWeight: '500' }}>
                                                                {ticket.ticket_id}
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: '12px 10px' }}>{ticket.subject || '-'}</td>
                                                        <td style={{ padding: '12px 10px' }}>
                                                            <span style={{
                                                                display: 'inline-block',
                                                                padding: '3px 10px',
                                                                borderRadius: '4px',
                                                                fontSize: '12px',
                                                                fontWeight: '500',
                                                                backgroundColor: ticket.form_type === 'general' ? '#e8f5e9' : '#e3f2fd',
                                                                color: ticket.form_type === 'general' ? '#27ae60' : '#3498db'
                                                            }}>
                                                                {ticket.form_type}
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: '12px 10px' }}>{ticket.form_name || '-'}</td>
                                                        <td style={{ padding: '12px 10px' }}>{formatDate(ticket.due_date)}</td>
                                                        <td style={{ padding: '12px 10px' }}>
                                                            <span style={{
                                                                display: 'inline-block',
                                                                padding: '3px 12px',
                                                                borderRadius: '20px',
                                                                fontSize: '12px',
                                                                fontWeight: '600',
                                                                color: 'white',
                                                                backgroundColor: getStatusColor(ticket.status)
                                                            }}>
                                                                {ticket.status}
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: '12px 10px' }}>{formatDate(ticket.created_at)}</td>
                                                        <td style={{ padding: '12px 10px' }}>
                                                            {ticket.status === 'Closed' ? 
                                                                (ticket.closed_by_name || ticket.closed_by || 'System') : 
                                                                '-'
                                                            }
                                                        </td>
                                                        <td style={{ padding: '12px 10px' }}>
                                                            {ticket.closed_date ? formatDateTime(ticket.closed_date) : '-'}
                                                        </td>
                                                        <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                                                            <button 
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    viewTicketDetails(ticket);
                                                                }}
                                                                style={{
                                                                    padding: '5px 12px',
                                                                    backgroundColor: selectedTicket?.id === ticket.id ? '#e74c3c' : '#3498db',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    fontSize: '12px',
                                                                    fontWeight: '500',
                                                                    transition: 'background-color 0.2s'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    if (selectedTicket?.id === ticket.id) {
                                                                        e.target.style.backgroundColor = '#c0392b';
                                                                    } else {
                                                                        e.target.style.backgroundColor = '#2980b9';
                                                                    }
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    if (selectedTicket?.id === ticket.id) {
                                                                        e.target.style.backgroundColor = '#e74c3c';
                                                                    } else {
                                                                        e.target.style.backgroundColor = '#3498db';
                                                                    }
                                                                }}
                                                            >
                                                                <span>{selectedTicket?.id === ticket.id ? '🔽' : '👁️'}</span>
                                                                {selectedTicket?.id === ticket.id ? 'Hide' : 'View'}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    
                                                    {selectedTicket?.id === ticket.id && (
                                                        <tr>
                                                            <td colSpan="10" style={{ padding: '0', backgroundColor: '#f8f9fa' }}>
                                                                <div style={{
                                                                    padding: '20px',
                                                                    margin: '0',
                                                                    background: 'white',
                                                                    borderRadius: '8px',
                                                                    border: '1px solid #e0e0e0',
                                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                                                                }}>
                                                                    <h4 style={{ 
                                                                        margin: '0 0 16px 0', 
                                                                        color: '#2c3e50', 
                                                                        fontSize: '16px',
                                                                        borderBottom: '2px solid #3498db',
                                                                        paddingBottom: '10px',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '8px'
                                                                    }}>
                                                                        <span>📋</span> Ticket Details - {selectedTicket.ticket_id}
                                                                    </h4>
                                                                    
                                                                    <div style={{ 
                                                                        display: 'grid', 
                                                                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                                                                        gap: '16px',
                                                                        marginBottom: '16px'
                                                                    }}>
                                                                        <div style={{ backgroundColor: '#f8f9fa', padding: '12px', borderRadius: '6px' }}>
                                                                            <strong style={{ color: '#7f8c8d', fontSize: '11px', textTransform: 'uppercase' }}>Subject</strong>
                                                                            <p style={{ margin: '4px 0 0', fontSize: '14px' }}>{selectedTicket.subject}</p>
                                                                        </div>
                                                                        
                                                                        <div style={{ backgroundColor: '#f8f9fa', padding: '12px', borderRadius: '6px' }}>
                                                                            <strong style={{ color: '#7f8c8d', fontSize: '11px', textTransform: 'uppercase' }}>Form Type</strong>
                                                                            <p style={{ margin: '4px 0 0', fontSize: '14px' }}>
                                                                                <span style={{
                                                                                    display: 'inline-block',
                                                                                    padding: '3px 10px',
                                                                                    borderRadius: '4px',
                                                                                    fontSize: '12px',
                                                                                    fontWeight: '500',
                                                                                    backgroundColor: selectedTicket.form_type === 'general' ? '#e8f5e9' : '#e3f2fd',
                                                                                    color: selectedTicket.form_type === 'general' ? '#27ae60' : '#3498db'
                                                                                }}>
                                                                                    {selectedTicket.form_type}
                                                                                </span>
                                                                            </p>
                                                                        </div>
                                                                        
                                                                        <div style={{ backgroundColor: '#f8f9fa', padding: '12px', borderRadius: '6px' }}>
                                                                            <strong style={{ color: '#7f8c8d', fontSize: '11px', textTransform: 'uppercase' }}>Form Name</strong>
                                                                            <p style={{ margin: '4px 0 0', fontSize: '14px' }}>{selectedTicket.form_name || 'N/A'}</p>
                                                                        </div>
                                                                        
                                                                        <div style={{ backgroundColor: '#f8f9fa', padding: '12px', borderRadius: '6px' }}>
                                                                            <strong style={{ color: '#7f8c8d', fontSize: '11px', textTransform: 'uppercase' }}>Due Date</strong>
                                                                            <p style={{ margin: '4px 0 0', fontSize: '14px' }}>{formatDate(selectedTicket.due_date)}</p>
                                                                        </div>
                                                                        
                                                                        <div style={{ backgroundColor: '#f8f9fa', padding: '12px', borderRadius: '6px' }}>
                                                                            <strong style={{ color: '#7f8c8d', fontSize: '11px', textTransform: 'uppercase' }}>Status</strong>
                                                                            <p style={{ margin: '4px 0 0', fontSize: '14px' }}>
                                                                                <span style={{
                                                                                    display: 'inline-block',
                                                                                    padding: '3px 12px',
                                                                                    borderRadius: '20px',
                                                                                    fontSize: '12px',
                                                                                    fontWeight: '600',
                                                                                    color: 'white',
                                                                                    backgroundColor: getStatusColor(selectedTicket.status)
                                                                                }}>
                                                                                    {selectedTicket.status}
                                                                                </span>
                                                                            </p>
                                                                        </div>
                                                                        
                                                                        <div style={{ backgroundColor: '#f8f9fa', padding: '12px', borderRadius: '6px' }}>
                                                                            <strong style={{ color: '#7f8c8d', fontSize: '11px', textTransform: 'uppercase' }}>Created</strong>
                                                                            <p style={{ margin: '4px 0 0', fontSize: '14px' }}>{formatDateTime(selectedTicket.created_at)}</p>
                                                                        </div>
                                                                        
                                                                        <div style={{ backgroundColor: '#f8f9fa', padding: '12px', borderRadius: '6px' }}>
                                                                            <strong style={{ color: '#7f8c8d', fontSize: '11px', textTransform: 'uppercase' }}>Last Updated</strong>
                                                                            <p style={{ margin: '4px 0 0', fontSize: '14px' }}>{formatDateTime(selectedTicket.updated_at)}</p>
                                                                        </div>
                                                                        
                                                                        <div style={{ gridColumn: '1 / -1', backgroundColor: '#f8f9fa', padding: '12px', borderRadius: '6px' }}>
                                                                            <strong style={{ color: '#7f8c8d', fontSize: '11px', textTransform: 'uppercase' }}>Description</strong>
                                                                            <p style={{ 
                                                                                margin: '8px 0 0', 
                                                                                fontSize: '14px', 
                                                                                lineHeight: '1.6',
                                                                                backgroundColor: 'white',
                                                                                padding: '12px',
                                                                                borderRadius: '4px',
                                                                                border: '1px solid #e0e0e0'
                                                                            }}>
                                                                                {selectedTicket.description || 'No description provided'}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                    
                                                                    <ClosedTicketDetails ticket={selectedTicket} />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            
                            {tickets.length > 0 && (
                                <div style={{
                                    marginTop: '20px',
                                    padding: '16px 20px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '8px',
                                    border: '1px solid #e0e0e0',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '12px'
                                }}>
                                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                                        <div>
                                            <span style={{ color: '#7f8c8d', fontSize: '12px', fontWeight: '500' }}>Total Tickets</span>
                                            <h3 style={{ margin: '2px 0 0', color: '#2c3e50' }}>{tickets.length}</h3>
                                        </div>
                                        <div>
                                            <span style={{ color: '#7f8c8d', fontSize: '12px', fontWeight: '500' }}>Open</span>
                                            <h3 style={{ margin: '2px 0 0', color: '#28a745' }}>
                                                {tickets.filter(t => t.status === 'Open').length}
                                            </h3>
                                        </div>
                                        <div>
                                            <span style={{ color: '#7f8c8d', fontSize: '12px', fontWeight: '500' }}>Closed</span>
                                            <h3 style={{ margin: '2px 0 0', color: '#6c757d' }}>
                                                {tickets.filter(t => t.status === 'Closed').length}
                                            </h3>
                                        </div>
                                        <div>
                                            <span style={{ color: '#7f8c8d', fontSize: '12px', fontWeight: '500' }}>Forms</span>
                                            <h3 style={{ margin: '2px 0 0', color: '#3498db' }}>
                                                {tickets.filter(t => t.form_type === 'form').length}
                                            </h3>
                                        </div>
                                    </div>
                                    <div style={{ color: '#95a5a6', fontSize: '12px' }}>
                                        Last updated: {new Date().toLocaleString('en-IN')}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default Helpdesk;